import React, { useContext, useEffect, useState } from 'react'
import {
  ChangePresentContext,
  EventMetaContext,
  MedPlanContext,
  OpsPlanContext,
  RiskAssessmentContext,
} from '../SingleEvent'
import {
  Alert,
  Autocomplete,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SmallModal from '../../../../components/Modals/SmallModal'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'

const EMPMenu = ({ openMenu, setOpenMenu }) => {
  const { eventMeta } = useContext(EventMetaContext)
  const { changesPresent, setChangesPresent } = useContext(ChangePresentContext)
  const { opsPlan, setOpsPlan } = useContext(OpsPlanContext)
  const { riskAssessment, setRiskAssessment } = useContext(
    RiskAssessmentContext,
  )
  const { medPlan, setMedPlan } = useContext(MedPlanContext)
  const { setToast } = useContext(GlobalContext)

  const [openLoadFromTemplate, setOpenLoadFromTemplate] = useState(false)
  const [templateOptions, setTemplateOptions] = useState([])
  const [loadingTemplateOptions, setLoadingTemplateOptions] = useState(true)
  const [selectedEMPtemplate, setSelectedEMPTemplate] = useState(null)

  const [openSaveAsTemplate, setOpenSaveAsTemplate] = useState(false)
  const [openMarkAsRequired, setOpenMarkAsNotRequired] = useState(false)
  const [openEmailToAll, setOpenEmailToAll] = useState(false)
  const [newTemplateTitle, setNewTemplateTitle] = useState('')
  const [includeOpsPlan, setIncludeOpsPlan] = useState(true)
  const [includeRiskAssessment, setIncludeRiskAssessment] = useState(true)
  const [includeMedicalConsiderations, setIncludeMedicalConsiderations] =
    useState(true)

  useEffect(() => {
    if (openLoadFromTemplate) {
      API.get('/admin/emptemplates')
        .then((res) => {
          if (res.status === 200) {
            setTemplateOptions(res.data)
          }
        })
        .finally(() => setLoadingTemplateOptions(false))
    }
  }, [openLoadFromTemplate])

  const handleSaveTemplate = (e) => {
    e.preventDefault()
    let data = {
      title: newTemplateTitle,
    }
    if (includeOpsPlan) data.opsPlan = opsPlan
    if (includeRiskAssessment) data.riskAssessment = riskAssessment
    if (includeMedicalConsiderations) data.medPlan = medPlan

    API.post('/admin/emptemplate', data).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'EMP Template Saved' })
        setOpenMenu(false)
        setOpenSaveAsTemplate(false)
      } else {
        setToast({
          message: 'Unable to save template. Please try again later',
          severity: 'error',
        })
      }
    })
  }

  const handleNotRequired = () => {
    API.put('/edit-event', {
      eventID: eventMeta._id,
      empNotRequired: !!!eventMeta.empNotRequired,
    }).then((res) => {
      if (res.status === 200) {
        window.location.reload()
        // TODO: Make this pretty instead of just reloading
      }
    })
  }

  const handleLoadTemplate = (e) => {
    e.preventDefault()
    API.get(`/admin/emptemplate/${selectedEMPtemplate._id}`).then((res) => {
      if (res.status === 200) {
        setOpsPlan(res.data?.opsPlan ?? {})
        setMedPlan(res.data?.medPlan ?? {})
        setRiskAssessment(res.data?.riskAssessment ?? {})
        !changesPresent && setChangesPresent(true)
        setOpenLoadFromTemplate(false)
        setOpenMenu(false)
      }
    })
  }

  const sendEMPToStaff = () => {
    API.post(`/events/${eventMeta._id}/sendEMPtoAllStaff`).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'EMP sent to all staff on shift' })
        setOpenEmailToAll(false)
      }
    })
  }

  return (
    <>
      <Stack gap={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Templates
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <Button
            size="small"
            variant="contained"
            color="anBlue"
            fullWidth
            onClick={() => setOpenLoadFromTemplate(true)}
          >
            Load Template
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="anBlue"
            fullWidth
            onClick={() => setOpenSaveAsTemplate(true)}
          >
            Save Current as Template
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Reuse an existing EMP or save this plan for similar events.
        </Typography>
        <Divider />
      </Stack>
      <Menu
        open={!!openMenu}
        anchorEl={openMenu}
        onClose={() => setOpenMenu(null)}
      >
        <MenuItem onClick={() => setOpenLoadFromTemplate(true)}>
          Load from Template
        </MenuItem>
        <MenuItem onClick={() => setOpenSaveAsTemplate(true)}>
          Save as Template
        </MenuItem>
        <MenuItem onClick={() => setOpenMarkAsNotRequired(true)}>
          EMP {!eventMeta.empNotRequired && 'not '}Required
        </MenuItem>
        <MenuItem
          onClick={() => setOpenEmailToAll(true)}
          disabled={!eventMeta.EMPApproved}
        >
          Email to Staff
        </MenuItem>
      </Menu>
      <SmallModal
        style={{ width: '500px' }}
        open={openMarkAsRequired}
        onClose={() => setOpenMarkAsNotRequired(false)}
      >
        <Stack gap={1} alignItems="center">
          <Typography variant="title3">
            Mark EMP as {!eventMeta.empNotRequired && 'not '}required
          </Typography>
          <Typography variant="caption">
            Are you sure you want to mark the EMP as{' '}
            {!eventMeta.empNotRequired && 'not '}required for this event?
          </Typography>
          <ButtonGroup>
            <Button
              type="button"
              variant="contained"
              color="error"
              onClick={() => setOpenMarkAsNotRequired(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              color="success"
              onClick={() => handleNotRequired()}
            >
              Yes
            </Button>
          </ButtonGroup>
        </Stack>
      </SmallModal>
      <SmallModal
        style={{ width: '500px' }}
        open={openSaveAsTemplate}
        onClose={() => setOpenSaveAsTemplate(false)}
      >
        <Stack gap={1} component="form" onSubmit={handleSaveTemplate}>
          <Typography variant="title3">Save EMP as Template</Typography>
          <TextField
            value={newTemplateTitle}
            label="Title"
            required
            onChange={(e) => setNewTemplateTitle(e.target.value)}
          />
          <FormControlLabel
            label="Include Operational Plan"
            control={
              <Checkbox
                checked={includeOpsPlan}
                onChange={(e) => setIncludeOpsPlan(e.target.checked)}
              />
            }
          />
          <FormControlLabel
            label="Include Risk Assessment"
            control={
              <Checkbox
                checked={includeRiskAssessment}
                onChange={(e) => setIncludeRiskAssessment(e.target.checked)}
              />
            }
          />
          <FormControlLabel
            label="Include Medical Considerations"
            control={
              <Checkbox
                checked={includeMedicalConsiderations}
                onChange={(e) =>
                  setIncludeMedicalConsiderations(e.target.checked)
                }
              />
            }
          />
          <Button type="submit" variant="contained" fullWidth color="success">
            Submit
          </Button>
        </Stack>
      </SmallModal>
      <SmallModal
        style={{ width: '500px' }}
        open={openLoadFromTemplate}
        onClose={() => setOpenLoadFromTemplate(false)}
      >
        <Stack gap={1} component="form" onSubmit={handleLoadTemplate}>
          <Typography variant="title3">Load EMP Template</Typography>
          <Autocomplete
            fullWidth
            options={templateOptions}
            value={selectedEMPtemplate}
            loading={loadingTemplateOptions}
            isOptionEqualToValue={(opt, value) => opt._id === value._id}
            getOptionLabel={(option) => option.title}
            onChange={(e, value) => setSelectedEMPTemplate(value)}
            renderInput={(params) => (
              <TextField required label="Template" {...params} />
            )}
          />
          <Alert severity="warning">
            This will override any existing values.
          </Alert>
          <Button fullWidth color="success" type="submit" variant="contained">
            Submit
          </Button>
        </Stack>
      </SmallModal>
      <SmallModal
        open={openEmailToAll}
        onClose={() => setOpenEmailToAll(false)}
        autoWidth
      >
        <Stack gap={1} alignItems="center">
          <Typography variant="title3" textAlign="center">
            Email EMP to All Staff
          </Typography>
          <Typography variant="body2" textAlign="center">
            This will email a copy of the EMP to all staff on shift
          </Typography>
          <Button
            color="anRed"
            variant="outlined"
            fullWidth
            onClick={() => setOpenEmailToAll(false)}
          >
            Cancel
          </Button>
          <Button
            color="anGreen"
            variant="contained"
            fullWidth
            onClick={() => sendEMPToStaff()}
          >
            Send to All
          </Button>
        </Stack>
      </SmallModal>
    </>
  )
}

export default EMPMenu
